import type en from "./en";

type WidenStrings<T> = T extends string
  ? string
  : T extends object
    ? { readonly [K in keyof T]: WidenStrings<T[K]> }
    : T;

const ar: WidenStrings<typeof en> = {
  common: {
    appName: "OELOX الشريك",
    save: "حفظ",
    cancel: "إلغاء",
    close: "إغلاق",
    clear: "مسح",
    retry: "إعادة المحاولة",
    loading: "جاري التحميل…",
    create: "إنشاء",
    refresh: "تحديث",
    search: "بحث",
    online: "متصل",
    busy: "مشغول",
    offline: "غير متصل",
    active: "نشط",
    enabled: "مفعّل",
    disabled: "معطّل",
    manual: "يدوي",
    pickup: "استلام",
    delivery: "توصيل",
    unassigned: "غير معيّن",
    dash: "—",
    copy: "نسخ",
    copied: "تم النسخ.",
    copyFailed: "تعذّر النسخ.",
  },
  language: {
    select: "اختر اللغة",
    title: "اللغة",
    hint: "اختر العربية أو الكردية أو الإنجليزية لمساحة عمل الشريك.",
  },
  auth: {
    tag: "الشريك",
    loginTitle: "تسجيل دخول الشريك",
    loginSubtitle:
      "سجّل الدخول إلى مساحة عمل الشريك. حسابات الشركاء فقط — وليس حسابات الأعمال أو إدارة OELOX.",
    email: "البريد الإلكتروني",
    loginId: "اسم المستخدم أو البريد",
    emailPlaceholder: "اسم المستخدم أو you@partner.com",
    password: "كلمة المرور",
    passwordPlaceholder: "كلمة المرور",
    signIn: "تسجيل الدخول",
    signingIn: "جاري الدخول…",
    foot: "مساحة شريك آمنة · OELOX",
    hidePassword: "إخفاء كلمة المرور",
    showPassword: "إظهار كلمة المرور",
    authFailed: "فشل المصادقة.",
    notPartner: "هذا الحساب غير مرتبط بمساحة عمل شريك.",
    signInFailed: "تعذّر تسجيل الدخول. حاول مرة أخرى.",
    invalidCredentials: "اسم المستخدم أو كلمة المرور غير صحيحة.",
    emailNotConfirmed: "يرجى تأكيد بريدك الإلكتروني قبل تسجيل الدخول.",
    tooMany: "محاولات كثيرة جداً. حاول مرة أخرى بعد قليل.",
    networkError: "فشل الاتصال. تحقق من الإنترنت وحاول مجدداً.",
    signedIn: "تم تسجيل الدخول.",
    changePasswordTitle: "تعيين كلمة مرور جديدة",
    changePasswordSubtitle:
      "يتطلب حسابك تغيير كلمة المرور قبل فتح مساحة عمل الشريك.",
    newPassword: "كلمة المرور الجديدة",
    confirmPassword: "تأكيد كلمة المرور",
    updatePassword: "تحديث كلمة المرور",
    updatingPassword: "جاري التحديث…",
    passwordTooShort: "يجب أن تكون كلمة المرور 8 أحرف على الأقل.",
    passwordMismatch: "كلمتا المرور غير متطابقتين.",
    passwordUpdateFailed: "تعذّر تحديث كلمة المرور. حاول مرة أخرى.",
    newPartnerLink: "شريك جديد؟ فعّل حسابك برمز الدخول",
    passcodeTitle: "شريك جديد",
    passcodeSubtitle: "أدخل الرمز المكوّن من 6 أرقام من مسؤول OELOX لتفعيل حساب الشريك.",
    passcodeLabel: "رمز الدخول",
    passcodePlaceholder: "الرمز المكوّن من 6 أرقام",
    continue: "متابعة",
    checkingCode: "جاري التحقق…",
    back: "رجوع",
    backToSignIn: "رجوع لتسجيل الدخول",
    passcodeInvalid: "رمز غير صالح. تحقق وحاول مرة أخرى.",
    passcodeUsed: "تم استخدام هذا الرمز مسبقاً.",
    passcodeExpired: "انتهت صلاحية هذا الرمز. اطلب رمزاً جديداً.",
    passcodeAttempts: "محاولات كثيرة. اطلب رمزاً جديداً.",
    onboardingTitle: "إكمال الإعداد",
    onboardingSubtitle: "أنشئ بيانات الدخول لتفعيل حساب الشريك.",
    displayName: "اسمك",
    username: "اسم المستخدم",
    usernameHint: "3–30 حرفاً: a-z 0-9 . _ - — هذا هو معرّف الدخول",
    usernameInvalid: "اسم مستخدم غير صالح (3–30 حرفاً: a-z 0-9 . _ -).",
    usernameTaken: "اسم المستخدم مستخدم بالفعل. جرّب اسماً آخر.",
    emailOptional: "البريد (اختياري)",
    emailHint: "name@example.com",
    emailInvalid: "أدخل بريداً صالحاً أو اتركه فارغاً.",
    emailTaken: "البريد مسجّل مسبقاً.",
    activateAccount: "تفعيل الحساب",
    activatingAccount: "جاري التفعيل…",
    setupComplete: "تم تفعيل الحساب. سجّل الدخول للمتابعة.",
    partnerActivated: "تم تفعيل الحساب — أهلاً بك في OELOX Partner!",
    onboardingFailed: "فشل الإعداد. حاول مرة أخرى.",
  },
  nav: {
    dashboard: "لوحة التحكم",
    orders: "الطلبات",
    drivers: "السائقون",
    clients: "العملاء",
    analytics: "التحليلات",
    wallet: "المحفظة",
    settings: "الإعدادات",
  },
  shell: {
    openMenu: "فتح القائمة",
    notifications: "الإشعارات",
    settings: "الإعدادات",
    signOut: "تسجيل الخروج",
    workspaceFallback: "الشريك",
  },
  overview: {
    eyebrow: "مركز القيادة",
    titleFallback: "الشريك",
    subtitle: "اطّلع على ما يحدث الآن — الطلبات والسائقون والعملاء بنظرة واحدة.",
    loadError: "تعذّر تحميل الطلبات المباشرة. تحقق من الجلسة وحاول مرة أخرى.",
    updatedLive: "يُحدَّث مباشرة",
    date: {
      ariaGroup: "نطاق التاريخ",
      today: "اليوم",
      custom: "مخصص",
      showing: "العرض",
      pickerEyebrow: "نطاق التاريخ",
      pickerTitle: "فترة مخصصة",
      pickerSubtitle: "تصفية نشاط لوحة التحكم حسب التاريخ.",
      presets: "اختصارات",
      presetYesterday: "أمس",
      presetLast7: "آخر 7 أيام",
      presetLast30: "آخر 30 يوماً",
      presetThisMonth: "هذا الشهر",
      from: "من",
      to: "إلى",
      confirm: "تطبيق",
    },
    flow: {
      title: "مسار الطلبات المباشر",
      liveBadge: "مباشر",
      hint: "اضغط على مرحلة لتصفية الطلبات أدناه",
      activeNow: "نشط الآن",
      ordersLabel: "طلبات",
      aria: "مسار الطلبات المباشر",
    },
    recentOrders: {
      title: "أحدث الطلبات",
      subtitle: "آخر الطلبات — اضغط على صف لفتحه في الطلبات.",
      empty: "لا توجد طلبات في هذا العرض",
      filteredBy: "مصفّى حسب",
    },
    activeClients: {
      title: "العملاء النشطون",
      subtitle: "أحدث النشاط بين عملائك.",
      viewAll: "عرض الكل",
      empty: "لا يوجد نشاط بعد",
      ordersCount: "{count} طلبات",
      openCount: "{count} مفتوح",
      justNow: "الآن",
      noOrders: "لا طلبات بعد",
    },
    dispatch: {
      aria: "ذكاء الإرسال",
      driverStatus: "حالة السائقين",
      driverCaption: "متصل {online} · مشغول {busy} · غير متصل {offline}",
      activeLoad: "الحمل النشط",
      loadCaption: "قيد التوصيل {inDelivery} · غير معيّن {unassigned}",
      fallback: "احتياطي OELOX",
      fallbackCaption: "للقراءة فقط · مستخدم اليوم —",
      wallet: "رصيد المحفظة",
      walletCaption: "حد الائتمان {limit}",
      walletLow: "رصيد منخفض — يرجى التعبئة",
      walletBlocked: "تحت الحد — الإرسال متوقف",
    },
  },
  orders: {
    title: "الطلبات",
    eyebrow: "مركز العمليات",
    subtitle:
      "تابع الطلبات الواردة واقبلها وأرسلها إلى السائقين — يتم التحديث تلقائياً.",
    liveTitle: "الطلبات المباشرة",
    searchPlaceholder: "ابحث عن عميل أو ضيف أو هاتف…",
    searchAria: "البحث في الطلبات",
    emptyTitle: "لا توجد طلبات في هذه المرحلة",
    emptyHint: "تُعرض فقط طلبات عملاء الشريك.",
    emptyListening: "تظهر الطلبات الجديدة هنا تلقائياً — أبقِ هذه الشاشة مفتوحة.",
    emptyTitleStage: "لا توجد طلبات في مرحلة «{stage}» الآن",
    emptyRangeHint: "تبحث عن طلبات أقدم؟ غيّر النطاق الزمني من الشريط العلوي.",
    showAllOrders: "عرض كل الطلبات",
    backToList: "رجوع إلى الطلبات",
    stagesAria: "مراحل الطلب",
    stages: {
      pending: "انتظار",
      processing: "تحضير",
      ready: "جاهز",
      in_delivery: "توصيل",
      completed: "مكتمل",
      cancelled: "ملغى",
      all: "الكل",
    },
    stagesShort: {
      pending: "بالانتظار",
      processing: "تحضير",
      ready: "جاهز",
      in_delivery: "توصيل",
      completed: "مكتمل",
      cancelled: "ملغى",
      all: "الكل",
    },
    method: {
      whatsappAi: "واتساب AI",
      whatsapp: "واتساب",
      manual: "يدوي",
      phone: "هاتف",
      webStore: "متجر الويب",
      pos: "نقطة بيع",
      marketplace: "السوق",
    },
    kpis: {
      processing: "قيد المعالجة",
      ready: "جاهز",
      inDelivery: "قيد التوصيل",
    },
    updatedNow: "تم التحديث الآن",
    updatedAgo: "آخر تحديث {value}",
    showing: "عرض {shown} من {total}",
    sort: {
      label: "ترتيب الطلبات",
      newest: "الأحدث أولاً",
      oldest: "الأقدم أولاً",
      amount: "الأعلى قيمة",
    },
    waitNow: "الآن",
    accept: "قبول الطلب",
    acceptShort: "قبول",
    decline: "رفض الطلب",
    declineConfirm: "رفض هذا الطلب؟ سيغادر قائمتك وسيتم إشعار المتجر.",
    declineReasonPrompt: "السبب (اختياري)",
    declineModal: {
      title: "رفض الطلب",
      subtitle: "سيغادر الطلب قائمتك وسيتم إشعار المتجر.",
      reasonLabel: "السبب",
      reasons: {
        busy: "مشغول جداً الآن",
        far: "بعيد / خارج النطاق",
        closed: "مغلق أو غير متاح",
        other: "أخرى",
      },
      noteLabel: "ملاحظة (اختياري)",
      notePlaceholder: "ملاحظة قصيرة للمتجر…",
      confirm: "رفض الطلب",
    },
    assign: "تعيين سائق",
    assignShort: "تعيين",
    changeShort: "تغيير",
    needsDriver: "بحاجة إلى سائق",
    detailEmpty: "اختر طلباً",
    detailEmptyHint: "انقر على أي طلب في القائمة لعرض تفاصيله هنا.",
    items: "العناصر",
    notes: "ملاحظات",
    detail: {
      customer: "العميل",
      timeline: "المسار الزمني",
      created: "أُنشئ",
      accepted: "تم القبول",
      copyNumber: "نسخ رقم الطلب",
      returnToStore: "مرفوض · إرجاع للمتجر",
      copyPhone: "نسخ رقم الهاتف",
      call: "الاتصال بالعميل",
      openMap: "فتح في الخرائط",
      copyAddress: "نسخ العنوان",
      more: "المزيد",
      changeDriver: "تغيير السائق",
      noDriverAssigned: "لم يُعيَّن سائق بعد.",
      next: {
        pending: "الخطوة التالية: اقبل الطلب ثم اختر سائق التوصيل.",
        driver: "الخطوة التالية: عيّن سائقاً لتوصيل هذا الطلب.",
        onTheWay: "الطلب مع السائق — تابع حالة التوصيل.",
      },
      itemsCount: "{count} عناصر",
      km: "{value} كم",
      distance: "المسافة",
      payment: "الدفع",
      paymentType: {
        prepaid: "مدفوع مسبقاً",
        cod: "الدفع عند الاستلام",
        payAtCashier: "دفع عند الكاشير",
        unknown: "الدفع غير محدد",
      },
      method: "الطريقة",
      methodCash: "نقداً",
      methodCard: "بطاقة",
      methodWallet: "محفظة",
      methodCod: "الدفع عند الاستلام",
      statusLabel: "الحالة",
      statusPaid: "مدفوع",
      statusUnpaid: "غير مدفوع",
      statusCodCollected: "تم تحصيل النقد",
      statusPickupCollected: "مدفوع عند الاستلام",
      statusFailed: "فشل الدفع",
      statusRefunded: "مسترد",
      cashCollector: "التحصيل",
      driverCollects: "السائق يحصّل نقداً",
      deliveryFee: "رسم التوصيل",
      feePayerCustomer: "العميل يدفع",
      feePayerRestaurant: "المطعم يدفع",
      feePayerOelox: "OELOX يدفع",
      feePayerPartner: "الشريك يدفع",
      feePayerFree: "توصيل مجاني",
      clientCharge: "رسم الشريك",
      partnerFeePerOrder: "لكل طلب",
      partnerFeePercentage: "نسبة",
      prep: "التحضير",
      prepMinutes: "~{minutes} د",
      placed: "أُنشئ في",
      pickupReady: "جاهز للاستلام",
      total: "المجموع",
    },
    assignSection: "تعيين سائق",
    requestOelox: "طلب سائقي OELOX",
    reassign: "إعادة التعيين",
    clearDriver: "إزالة السائق",
    noDrivers: "لا يوجد سائقون بعد. أضف سائقين أو اطلب احتياطي OELOX.",
    acceptModal: {
      title: "قبول الطلب",
      dispatchTitle: "إرسال · {business}",
      partnerDrivers: "سائقو الشريك",
      steps: "1) اقبل الطلب · 2) اختر سائقاً من القائمة — أو أرسله إلى الجميع.",
      selectDriversHint: "اختر سائقاً واحداً للتعيين، أو عدة سائقين للبث.",
      sendToAll: "إرسال لجميع السائقين المتاحين والمشغولين",
      sendToAllHint: "أي سائق شريك مؤهل يمكنه قبول هذا التوصيل.",
      selectOeloxGlobal: "اختيار سائقي OELOX العالميين",
      oeloxNoDrivers: "لا يوجد سائقو OELOX عالميون متصلون الآن.",
      requestAnyOelox: "دع OELOX يختار سائقاً بدلاً من ذلك",
      oeloxOnline: "متصل",
      oeloxOffline: "غير متصل",
      assignDriver: "تعيين سائق",
      blastSelected: "بث للسائقين المحددين",
      dispatch: "إرسال",
      acceptOnly: "قبول فقط",
      acceptAndAssign: "قبول وتعيين سائق",
      searchDrivers: "ابحث عن سائقين…",
      noDriverMatch: "لا يوجد سائقون مطابقون لبحثك.",
      selectedCount: "{count} محدد",
      bypassModeHint:
        "وضع التجاوز يبث تلقائياً عند القبول. انتقل للإرسال اليدوي لاختيار السائقين.",
      noPartnerDrivers: "لا يوجد سائقو شريك في أسطولك.",
      noAssignableDrivers: "لا يوجد سائقو شريك متصلون أو مشغولون الآن.",
    },
    toast: {
      accepted: "تم قبول الطلب.",
      acceptedNoDrivers: "تم القبول — لا يوجد سائقو شريك متاحون.",
      acceptedBlast: "تم القبول وبُثّ لسائقيك.",
      acceptedDispatched: "تم القبول وإشعار السائق.",
      dispatched: "تم إشعار السائقين.",
      assigned: "تم تعيين السائق.",
      reassigned: "تمت إعادة تعيين السائق.",
      cleared: "تمت إزالة السائق.",
      oeloxRequested: "تم طلب سائق OELOX.",
      oeloxAssigned: "تم تعيين سائق OELOX.",
      declined: "تم رفض الطلب.",
      newOrder: "طلب جديد",
      view: "عرض",
    },
  },
  drivers: {
    title: "السائقون",
    searchPlaceholder: "ابحث عن سائقين…",
    searchAria: "البحث عن السائقين",
    add: "إضافة سائق",
    refresh: "تحديث",
    emptyTitle: "لا يوجد سائقون بعد",
    emptyHint: "أنشئ سائقي أسطول الشريك لتعيين التوصيلات.",
    createTitle: "إضافة سائق",
    name: "الاسم",
    namePlaceholder: "اسم السائق",
    phone: "الهاتف",
    vehicleType: "نوع المركبة",
    vehicleNumber: "رقم المركبة",
    bicycle: "دراجة",
    motorcycle: "دراجة نارية",
    car: "سيارة",
    creating: "جاري الإنشاء…",
    create: "إنشاء",
    modePasscode: "إضافة برمز المرور",
    modeReady: "جاهز للاستخدام",
    passcodeHint:
      "يُنشئ سائقاً في أسطول الشريك برمز مرور لمرة واحدة لتفعيله في تطبيق السائق.",
    readyHint:
      "يُنشئ حساب السائق الآن ببيانات الدخول أدناه. سلّمها مرة واحدة.",
    address: "العنوان",
    username: "اسم المستخدم",
    usernameHint: "3–30 حرفاً: a-z، 0-9، نقطة، شرطة، شرطة سفلية.",
    createReady: "إنشاء الحساب",
    readyCredentialsHint:
      "يمكن للسائق تسجيل الدخول إلى تطبيق السائق الآن بهذه البيانات.",
    resetPassword: "إعادة تعيين كلمة المرور",
    deactivate: "إلغاء التفعيل",
    passcode: "رمز المرور",
    modeLabel: "نوع التعاقد",
    mode: {
      own: "موظف",
      paid: "متعاقد",
    },
    modeOwnHint: "موظف ضمن فريقك — المركبة على حسابك، ولا يُحتسب له أجر لكل رحلة.",
    modePaidHint: "متعاقد مستقل — بمركبته الخاصة، ويُحتسب له أجر لكل رحلة عبر محفظته.",
    modeToastChanged: "تم تحديث نوع التعاقد.",
    tripPayTitle: "أجر الرحلة",
    tripPayLabel: "أجر السائق لكل رحلة (IQD)",
    tripPayPlaceholder: "مثال 2000",
    tripPayUsesDefault: "الافتراضي للشريك: {amount} — اتركه فارغاً لاستخدامه.",
    tripPayHint: "اتركه فارغاً لاستخدام الأجر الافتراضي للشريك.",
    tripPaySaved: "تم تحديث أجر السائق.",
    toast: {
      created: "تم إنشاء السائق.",
      deactivated: "تم إلغاء تفعيل السائق.",
      activated: "تم تفعيل السائق.",
      updated: "تم تحديث السائق.",
    },
    presence: {
      online: "متصل",
      busy: "مشغول",
      offline: "غير متصل",
    },
    subtitle: "أسطولك — الحضور المباشر والأجور والمحافظ.",
    columns: {
      name: "السائق",
      status: "الحالة",
      vehicle: "المركبة",
      activeOrders: "طلبات نشطة",
      mode: "الوضع",
      pay: "أجر الرحلة",
      wallet: "المحفظة",
    },
    filterAll: "كل الحالات",
    payDefaultMark: "افتراضي",
    back: "رجوع إلى السائقين",
    notFound: "السائق غير موجود",
    tabs: {
      settings: "إعدادات السائق",
      reports: "التقارير",
      operations: "التشغيل",
      overview: "نظرة عامة",
      wallet: "المحفظة",
      payouts: "الأجور",
      mode: "الوضع",
    },
    tabsAria: "أقسام السائق",
    deactivateTitle: "إلغاء تفعيل السائق؟",
    deactivateBody: "سيتوقف عن استلام الطلبات وتتم إزالته من قوائم الإرسال.",
    inactive: "غير مفعّل",
    activate: "تفعيل",
    whatsapp: "واتساب",
    editNotAvailable: "للقراءة فقط حالياً — التعديل يتطلب دعماً من الخادم.",
    missingFields:
      "غير موجود بعد في الخادم: عنوان السائق، الهوية الوطنية ومستندات إضافية.",
    vehicleModel: "موديل المركبة",
    vehicleColor: "اللون",
    vehicleYear: "سنة الصنع",
    documents: "المستندات",
    licenseImage: "صورة رخصة القيادة",
    selfPhoto: "الصورة الشخصية",
    open: "فتح",
    invalidYear: "أدخل سنة صحيحة (1980–2100).",
    assignExisting: {
      cta: "إضافة سائق مسجّل",
      title: "إضافة سائق لديه حساب مسبقاً",
      subtitle:
        "ابحث عنه برقم الهاتف. إذا كان مرتبطاً بعملاء، سيُفكّ ارتباطه بعد التأكيد — التوصيلات الجارية تمنع الإضافة.",
      search: "بحث",
      notFound: "لا يوجد سائق بهذا الرقم.",
      ownedByYou: "هذا السائق موجود في أسطولك.",
      ownedOther: "هذا السائق ينتمي إلى شريك آخر بالفعل.",
      sharedDriver: "سائق مشترك",
      clientsLabel: "مرتبط حالياً بـ:",
      unlinkNote: "بالتأكيد سيُفكّ ارتباطه من هؤلاء العملاء وتُضيفه إلى أسطولك.",
      move: "إضافته إلى أسطولي",
      moveWithUnlink: "فك الارتباط وإضافته إلى أسطولي",
      moved: "تمت إضافة السائق إلى أسطولك.",
      movedWithClients: "تمت إضافة السائق إلى أسطولك — فُكّ ارتباطه من: {clients}",
      alreadyYours: "السائق في أسطولك بالفعل.",
    },
    release: {
      title: "إخراج من الأسطول",
      hint: "إخراج هذا السائق من أسطولك. يصبح غير مرتبط ويمكنه العودة لاحقاً عبر رمز أو شريك آخر.",
      cta: "إخراج",
      toast: "تم إخراج السائق من أسطولك.",
    },
    reports: {
      orderNo: "الطلب",
      client: "العميل",
      ordersHistory: "سجل الطلبات",
      noOrders: "لا طلبات لهذا السائق بعد",
      currentOrders: "الطلبات الحالية",
      tripHistory: "سجل الرحلات",
      noTrips: "لا رحلات مكتملة بعد",
      noCurrentOrders: "لا طلبات نشطة معيّنة",
      limitNote: "آخر 25 رحلة",
    },
  },
  clients: {
    title: "العملاء",
    searchPlaceholder: "ابحث عن الأعمال…",
    searchAria: "البحث عن العملاء",
    add: "إضافة عميل",
    refresh: "تحديث",
    emptyTitle: "لا يوجد عملاء شريك بعد",
    emptyHint: "ستظهر هنا الأعمال المرتبطة أو المنشأة كهوية شريك.",
    createTitle: "إضافة عميل",
    businessName: "اسم النشاط",
    phone: "الهاتف",
    address: "العنوان",
    addressPlaceholder: "الشارع، المدينة…",
    emailOptional: "البريد (اختياري — للفواتير لاحقاً)",
    username: "اسم المستخدم (معرّف دخول العميل)",
    usernameHint:
      "3–30 حرفاً: a-z، 0-9، شرطة فقط. يسجّل العميل الدخول بهذا — وليس بالبريد.",
    edit: "تعديل العميل",
    editTitle: "تعديل العميل",
    editHint: "اسم المستخدم هو معرّف دخول العميل. البريد اختياري — للفواتير.",
    delete: "حذف",
    deleteTitle: "حذف العميل؟",
    deleteBody:
      "حذف ناعم: تصبح الحالة «ملغي» وتُلغى رموز التفعيل النشطة. يبقى السجل في النظام.",
    deleteConfirmCta: "نعم، احذف {name}",
    save: "حفظ التغييرات",
    creating: "جاري الإنشاء…",
    create: "إنشاء + رمز",
    modePasscode: "إضافة برمز المرور",
    modeReady: "جاهز للاستخدام",
    readyHint:
      "يُنشئ حساب المتجر الآن ببيانات الدخول أدناه. سلّمها مرة واحدة — يمكن للمالك تغيير كلمة المرور لاحقاً.",
    slug: "الرابط العام للمتجر",
    slugHint: "الرابط العام: /store/<slug>",
    createReady: "إنشاء الحساب",
    readyCredentialsHint:
      "يمكن للمتجر تسجيل الدخول الآن. يبقى متجره العام مخفياً حتى يفعّله المالك من الإعدادات.",
    passcodeTitle: "رمز تفعيل النشاط",
    passcodeHint:
      "شاركه مرة واحدة لتفعيل النشاط. تُضبط الهوية كشريك مع controlling_partner_id الخاص بك.",
    dismiss: "إخفاء",
    reissue: "الرمز",
    passcodeModalHint:
      "استخدام واحد — ينتهي بعد ساعتين تقريباً. شاركه مع المتجر لتفعيل حسابه.",
    openOrders: "طلبات مفتوحة",
    active: "نشط",
    inactive: "غير نشط",
    unknownStatus: "غير معروف",
    openOrdersKpi: "طلبات مفتوحة",
    noActivity: "لا نشاط بعد",
    driverPrefix: "السائق: {name}",
    toast: {
      created: "تم إنشاء العميل كعميل شريك.",
      updated: "تم تحديث العميل.",
      deleted: "تم حذف العميل — الحالة ملغي.",
    },
    eyebrow: "سجل العملاء",
    subtitle: "الأعمال التي يديرها الشريك — الطلبات ورسوم التوصيل وما هو مستحق عليهم.",
    totalKpi: "العملاء",
    trial: "تجريبي",
    onboarding: "قيد التفعيل",
    filterAll: "الكل",
    filtersAria: "تصفية حسب حالة العميل",
    colClient: "العميل",
    colStatus: "الحالة",
    colOrders: "الطلبات",
    colCompleted: "مكتملة",
    colFees: "رسوم التوصيل",
    colCharges: "رسوم الخدمة",
    colOutstanding: "المستحق",
    colLastOrder: "آخر طلب",
    colOpen: "مفتوح",
    view: "عرض",
    note: "الطلبات والرسوم إجماليات تراكمية؛ المستحق يعرض الرصيد غير المسدد.",
  },
  clientView: {
    back: "رجوع إلى العملاء",
    notFound: "العميل غير موجود.",
    tabsAria: "أقسام تقرير العميل",
    tabs: {
      overview: "نظرة عامة",
      orders: "الطلبات",
      statement: "الرسوم وكشف الحساب",
      drivers: "السائقون",
    },
    ordersRange: "الطلبات (النطاق)",
    activeNow: "نشط الآن",
    deliveryIncome: "إيراد التوصيل",
    clientCharges: "رسوم الخدمة",
    outstanding: "المستحق",
    driversNow: "سائقون نشطون",
    businessInfo: "معلومات النشاط",
    login: "معرّف الدخول",
    ownerEmail: "بريد المالك",
    linkedAt: "تاريخ الربط",
    dispatchAuthority: "صلاحية الإرسال",
    currency: "العملة",
    terms: "الشروط التجارية",
    termsHint: "الشروط التجارية يضبطها فريق إدارة OELOX.",
    noOrdersRange: "لا توجد طلبات في هذا النطاق.",
    legacyNote: "{count} توصيلات مكتملة بدون رسم توصيل مسعّر (بيانات قديمة).",
  },
  clientOrders: {
    searchPlaceholder: "ابحث في الطلبات…",
    searchAria: "البحث في طلبات العميل",
    stageFilter: "مرحلة الطلب",
    fulfillmentFilter: "نوع الطلب",
    export: "تصدير CSV",
    colDate: "التاريخ",
    colOrder: "الطلب",
    colCustomer: "الزبون",
    colDriver: "السائق",
    colStatus: "الحالة",
    colTotal: "الإجمالي",
    colDeliveryFee: "رسم التوصيل",
    colResponsibility: "دافع الرسم",
    colCharge: "الرسم",
    colPayment: "الدفع",
    colKm: "كم",
    showing: "عرض {from}–{to} من {total}",
    prev: "السابق",
    next: "التالي",
  },
  statement: {
    charged: "المحتسب",
    settled: "المسدد",
    pendingInRange: "معلق (النطاق)",
    outstanding: "المستحق",
    cancelledFees: "{count} طلبات ملغاة مع رسم",
    feeMix: "توزيع دافع رسم التوصيل",
    summary: "الملخص",
    instantTotal: "إجمالي الرسوم الفورية",
    voidedTotal: "الملغى في النطاق",
    wallet: "المحفظة",
    walletBalance: "رصيد المحفظة",
    walletPending: "ائتمان معلق",
    serviceFeeNote: "تُسجل رسوم الخدمة لكل طلب؛ سدّد بنود الائتمان لتصفية المعلقات.",
    ledger: "قيود السجل",
    empty: "لا رسوم في هذا النطاق.",
    emptyLedger: "لا رسوم مسجلة بعد.",
    recentLedger: "أحدث الرسوم",
    settle: "تسديد",
    settleToast: "تم تسديد البند.",
    settleFailed: "تعذّر تسديد البند.",
    colDate: "التاريخ",
    colOrder: "الطلب",
    colType: "النوع",
    colOrderTotal: "إجمالي الطلب",
    colFee: "الرسم",
    colMode: "الوضع",
    colStatus: "الحالة",
    colPayer: "الدافع",
    colOrders: "طلبات",
    colFees: "الرسوم",
  },
  wallet: {
    title: "محفظة السائق",
    owesDriver: "الشريك مدين للسائق",
    driverOwes: "السائق مدين للشريك",
    settled: "مسوّى — لا مستحقات",
    empty: "لا قيود في المحفظة بعد.",
    amountPlaceholder: "المبلغ (+ أو −)",
    notePlaceholder: "ملاحظة (اختياري)",
    addEntry: "إضافة قيد",
    entryHint:
      "+ يعني أنك مدين للسائق أكثر · − يعني أن السائق مدين لك (مثل قبض نقدي).",
    toastEntryAdded: "تمت إضافة قيد المحفظة.",
    type: {
      trip_fee: "رسم التوصيل",
      cod_collected: "نقد مقبوض من العميل",
      adjustment: "قيد يدوي",
      topup: "شحن المحفظة",
      trip_charge: "خصم رحلة",
    },
  },
  clientWallet: {
    title: "محفظة العميل",
    balance: "الرصيد",
    limit: "الحد السلبي",
    empty: "لا حركة في المحفظة بعد.",
    topupPlaceholder: "مبلغ الشحن",
    topup: "شحن",
    limitPlaceholder: "الحد (صفر أو سالب)",
    setLimit: "حفظ الحد",
    hint: "تُخصم رسوم التوصيل لكل رحلة مكتملة؛ والشحن يعيد الرصيد. الحد السلبي حماية يدوية — غير مطبّقة بعد.",
    toastTopup: "تم شحن المحفظة.",
    toastLimit: "تم حفظ الحد.",
    limitInvalid: "يجب أن يكون الحد صفراً أو رقماً سالباً.",
    failed: "فشل تحديث المحفظة.",
  },
  clientLedger: {
    title: "أجور التوصيل المستحقة للشريك",
    balanceToPay: "المستحق للدفع",
    feesTotal: "إجمالي الأجور",
    paidTotal: "المدفوع",
    empty: "لا توجد مستحقات حالياً.",
    returnToStore: "إرجاع للمتجر",
    note: "أجور التوصيل التي يتحمّلها المتجر (توصيل مجاني للزبون) ولم تُدفع نقداً للسائق — تُسوّى مع الشريك.",
    payTitle: "تسجيل دفعة من المتجر",
    payHint: "سجّل المبلغ الذي استلمته من المتجر — يُخصم مباشرةً من المستحق.",
    payAmount: "المبلغ",
    payNote: "ملاحظة (اختياري)",
    payCurrent: "المستحق حالياً: {amount}",
    payAfter: "بعد الدفع: {amount}",
    paySubmit: "تسجيل الدفعة",
    payToast: "تم تسجيل دفعة {amount}.",
    paymentsTitle: "الدفعات المسجّلة",
    owesPartner: "على العميل للشريك",
    partnerOwes: "على الشريك للعميل",
    net: "الصافي",
  },
  walletV2: {
    title: "محفظة الشريك",
    hint: "محفظة الفوترة الموحّدة — رسوم المنصة وتسويات أجور التوصيل فقط.",
    blockedBanner:
      "تم إيقاف الإرسال: المحفظة تحت حد الائتمان المسموح. اطلب من OELOX تعبئة الرصيد لاستئناف الإرسال — عمليات المتجر غير متأثرة.",
    lowBanner:
      "رصيد المحفظة عند أو أقل من حد التنبيه — عبّئ الرصيد قريباً لتجنّب توقف الإرسال.",
    balanceLabel: "الرصيد",
    statusHealthy: "سليم",
    statusLow: "رصيد منخفض",
    statusBlocked: "محظور",
    updatedAt: "آخر تحديث {time}",
    creditLimit: "حد الائتمان",
    creditLimitHint: "يتوقف الإرسال عند النزول تحت هذا الرصيد.",
    alertThreshold: "حد التنبيه",
    alertThresholdHint: "عتبة تنبيه الرصيد المنخفض.",
    feeOnlyNote:
      "تتابع المحفظة رسوم المنصة ٢٥٠ د.ع لكل طلب مكتمل وتسويات أجور التوصيل — قيم الطعام/الطلبات لا تدخل المحفظة إطلاقاً.",
    totalsLine: "استخدام المنصة {fees} · إجمالي التعبئة {topups} · {count} حركة",
    transactionsTitle: "الحركات",
    empty: "لا توجد حركات بعد.",
    colDate: "التاريخ",
    colType: "النوع",
    colDescription: "الوصف",
    colAmount: "المبلغ",
    colBalanceAfter: "الرصيد بعد",
    fleetTitle: "محافظ سائقي الأسطول",
    fleetTotals: "للتسليم: {cash} · مستحق الدفع: {due}",
    fleetEmpty: "لا يوجد سائقو أسطول بعد.",
    fleetNote:
      "محافظ السائقين: + يعني أن السائق مدين نقداً؛ − يعني وجود مستحق للدفع. سوِّ من لوحة السائق.",
    colDriver: "السائق",
    colBalance: "الرصيد",
    colCashHeld: "نقد بحوزته",
    colPayoutDue: "مستحق الدفع",
    txType: {
      topup: "تعبئة",
      platform_fee: "رسوم المنصة",
      adjustment: "تسوية",
      refund: "استرجاع",
      cash_collected: "نقد محصّل",
      trip_earnings: "أرباح الرحلة",
      cash_handover: "تسليم نقدي",
      partner_margin: "حصة الشريك",
      payout: "دفعة",
      order_value_collected: "قبض قيمة الطلب",
      rejection_refund: "استرجاع طلب مرفوض",
      rejection_fee_keep: "أجرة سائق (طلب مرفوض)",
      other: "قيد",
    },
    txDesc: {
      cashCollected: "نقد محصّل عند التسليم",
      orderValueCollected: "قيمة الطلب محصّلة عند التسليم",
      tripEarnings: "أرباح التوصيل",
      partnerShareAuto: "حصة الشريك بالتوصيل (خصم تلقائي)",
      marginDeducted: "حصة الشريك من الرحلة — تُخصم من الرصيد المسبق",
      feeReceived: "أجرة التوصيل المستلمة",
      platformFee: "رسوم استخدام المنصة",
      cashHandedOver: "تسليم نقدي للشريك",
      payoutToDriver: "دفعة إلى السائق",
      topup: "تعبئة رصيد المحفظة",
      rejectionRefund: "طلب مرفوض — استرجاع قيمة الطلب المدفوع في الكاشير",
      rejectionFeeKeep: "طلب مرفوض — أجرة السائق محتفظ بها بقرار الشريك",
    },
    driverOwes: "على السائق نقد للتسليم",
    payoutDue: "مستحق الدفع للسائق",
    settled: "مسوّى — لا مستحقات",
    cashOnHand: "النقد بحوزته",
    settlementsTitle: "تسويات الرحلات الأخيرة",
    colTripDate: "التاريخ",
    colTripFee: "أجرة التوصيل",
    colTripEarnings: "الأرباح",
    colTripCash: "النقد",
    colTripStatus: "الطلب",
    settleTitle: "حركة نقدية مع السائق",
    settleHandover: "تسليم نقدي",
    settlePayout: "دفعة",
    settleAmount: "المبلغ",
    settleNote: "السبب (اختياري)",
    settleSubmit: "تسجيل",
    settleTopup: "إضافة رصيد",
    settleHint:
      "تسليم: يعيد السائق النقد المحصّل (بحد النقد بحوزته). دفعة: سدّد المستحق (بحد المستحق للدفع).",
    topupHint:
      "إضافة رصيد لمحفظة السائق — تُسجّل كتسوية على الرصيد الصافي (للسلف/العهدة النقدية).",
    toastSettled: "تم تسجيل التسوية.",
    toastToppedUp: "تمت إضافة الرصيد إلى محفظة السائق.",
    stDriverOwes: "على السائق للشريك",
    stPartnerOwes: "على الشريك للسائق",
    stNet: "الرصيد الصافي",
    stWalletBalance: "رصيد المحفظة",
    stTripsCaption: "رحلات مكتملة",
    stIncomeCaption: "نصيبه من كل رحلة",
    stBalanceCaption: "رصيد التعبئة المقدّمة — تُخصم حصة الشريك لكل رحلة",
    stBalanceNegative: "رصيد سالب — أضف رصيداً لتسويته",
    addBalanceTitle: "إضافة رصيد",
    addBalanceHint:
      "يُضاف المبلغ إلى رصيد السائق المسبق — تُخصم حصة الشريك تلقائياً مع كل رحلة.",
    addBalanceSubmit: "إضافة الرصيد",
    addBalanceCurrent: "الحالي: {amount}",
    addBalanceAfter: "بعد الإضافة: {amount}",
    addBalanceNegative:
      "سيبقى سالباً (مستحقاً للتسوية) حتى يُضاف رصيد.",
    moveTake: "استلام من السائق",
    moveGive: "دفع للسائق",
    moveTakeHint: "تستلم من السائق نقداً (بحوزته الآن: {amount}).",
    moveTakeDue: "المطلوب استلامه: {amount}",
    moveGiveDue: "المستحق دفعه: {amount}",
    takeScopeLabel: "ماذا تستلم منه؟",
    scopeNetDesc: "كل المتبقي بذمته نقداً",
    scopeDeliveryDesc: "فروق أجور التوصيل المحصّلة",
    scopeOrderDesc: "قيم الطلبات المقبوضة نقداً",
    givePayoutDesc: "دفع المستحقات المتبقية للسائق",
    moveAmountDue: "المطلوب الآن: {amount} — قابل للتعديل",
    moveFillFull: "كامل المبلغ",
    moveRemainCash: "سيبقى بحوزة السائق {amount} بعد الاستلام.",
    moveRemainDue: "المتبقي مستحقاً بعد الدفع: {amount}",
    moveTopupPreview: "تُضاف {amount} رصيداً لمحفظة السائق (عهدة/سلفة).",
    movePreviewHint: "أدخل المبلغ لمعاينة الأثر.",
    giveReasonLabel: "سبب الدفع",
    givePayout: "سداد المستحق",
    givePayoutHint: "سدّد ما تبقّى من مستحقات السائق (الآن: {amount}).",
    giveTopup: "إضافة رصيد (سلفة)",
    giveTopupHint: "يضيف رصيداً على ما بيد السائق — للعهدة/السلف.",
    moveSubmitTake: "تسجيل الاستلام",
    moveSubmitGive: "تسجيل الدفع",
    toastReceived: "تم استلام {amount} من السائق.",
    toastPaid: "تم دفع {amount} للسائق.",
    toastCredited: "أُضيف {amount} رصيداً لمحفظة السائق.",
    duesWarning:
      "مستحقات محفظة غير مسوّاة — سوِّ الرصيد قبل نقل هذا العميل ليصبح تحت شريك.",
    consolidatedNote: "الفوترة تمر عبر محفظة الشريك الموحّدة.",
    breakdownTitle: "تفصيل الحساب",
    bdTrips: "إجمالي الرحلات",
    bdIncome: "أرباح السائق",
    bdOweDelivery: "على السائق للشريك (توصيل)",
    bdOweOrder: "على السائق للشريك (قيمة الطلب)",
    bdPartnerOwes: "على الشريك للسائق",
    bdNote: "الأرباح تبقى للسائق؛ البنود تعرض المتبقي بعد التسليمات الموجّهة (توصيل/قيمة طلب). التسليم النقدي والدفعات تسوّي الرصيد الصافي.",
    scopeLabel: "يُحتسب مقابل",
    scopeNet: "الصافي (الكل)",
    scopeDelivery: "فرق التوصيل",
    scopeOrder: "قيمة الطلب",
    scopeHint: "اختياري: خصّص المبلغ لفرق التوصيل أو قيمة الطلبات بدل الرصيد كامل.",
  },
  fees: {
    type: "نوع الرسم",
    amount: "قيمة الرسم",
    settlement: "التسوية",
    cashTarget: "تسوية النقد",
    perOrder: "لكل طلب",
    percentage: "نسبة مئوية",
    instant: "فوري إلى المحفظة",
    credit: "ائتمان (يُحصّل لاحقاً)",
    targetBusiness: "التسوية مع النشاط",
    targetPartner: "التسوية مع الشريك",
    unknown: "غير مهيأ",
  },
  ledger: {
    status: {
      pending: "معلق",
      settled: "مسدد",
      instant: "فوري",
      voided: "ملغى",
      other: "—",
    },
  },
  payment: {
    prepaid: "مدفوع مسبقاً",
    cod: "الدفع عند الاستلام",
    cashier: "الدفع عند الكاشير",
    unknown: "غير معروف",
  },
  responsibility: {
    customer: "الزبون",
    client: "العميل",
    free: "توصيل مجاني",
    oelox: "OELOX",
    other: "أخرى",
  },
  clientDrivers: {
    title: "نشاط السائقين",
    sourceNote: "من توصيلات أسطولك لهذا العميل — من سجل الطلبات فقط.",
    colDriver: "السائق",
    colVehicle: "المركبة",
    colTrips: "الرحلات",
    colKm: "كم",
    colCost: "كلفة السائق",
    colLastTrip: "آخر رحلة",
    empty: "لا رحلات سائقين في هذا النطاق.",
  },
  analytics: {
    eyebrow: "رؤى",
    title: "التحليلات",
    subtitle: "الطلبات وإيراد التوصيل ورسوم الخدمة والتحصيل للفترة المحددة.",
    allClients: "كل العملاء",
    all: "الكل",
    clientFilter: "تصفية حسب العميل",
    fulfillmentFilter: "تصفية حسب النوع",
    loadError: "تعذّر تحميل التحليلات. حدّث وحاول مرة أخرى.",
    kpiOrders: "الطلبات",
    kpiDeliveryIncome: "إيراد التوصيل",
    fromCustomers: "الزبائن",
    fromClients: "العملاء",
    kpiClientCharges: "رسوم الخدمة",
    kpiCod: "الدفع عند الاستلام",
    kpiOutstanding: "المستحق",
    pendingAdded: "أُضيف في النطاق: {amount}",
    kpiWallet: "رصيد المحفظة",
    kpiAvgFee: "متوسط رسم التوصيل",
    avgKm: "متوسط كم",
    avgPrep: "متوسط التحضير (دقيقة)",
    cancelledWithFee: "{count} ملغاة مع رسم",
    ordersPerDay: "الطلبات يومياً",
    incomePerDay: "إيراد التوصيل يومياً",
    byClient: "حسب العميل",
    byDriver: "حسب السائق",
    empty: "لا بيانات في هذا النطاق.",
    colClient: "العميل",
    colOrders: "الطلبات",
    colCompleted: "مكتملة",
    colCancelled: "ملغاة",
    colDeliveryFees: "رسوم التوصيل",
    colCharges: "رسوم الخدمة",
    colOutstanding: "المستحق",
    colDriver: "السائق",
    colTrips: "الرحلات",
    colKm: "كم",
    colLastTrip: "آخر رحلة",
    colDriverCost: "كلفة السائق",
    profitabilityTitle: "الربحية (هذا النطاق)",
    profitRevenue: "الإيراد (تقديري)",
    profitDriverCosts: "كلفة السائقين",
    profitOelox: "رسوم OELOX للرحلات",
    profitEstimated: "الربح التقديري",
    profitMarginValue: "هامش {pct}%",
    profitSettledTrips: "{count} رحلة مسوّاة",
    profitVoided: "ملغى: {amount}",
    profitNoRates:
      "حدّد أجر السائق لكل رحلة من الإعدادات ← الإرسال (أو لكل سائق) لاحتساب كلفة السائقين.",
    pendingNote:
      "تُحتسب كلفة السائقين من الأجور المحفوظة لكل رحلة؛ ورسوم OELOX من تسويات الرحلات.",
  },
  settings: {
    eyebrow: "مساحة العمل",
    title: "الإعدادات",
    subtitle: "ملف الشريك ووضع الإرسال والتفضيلات ومعلومات النظام.",
    segmentsAria: "أقسام الإعدادات",
    loadError: "تعذّر تحميل إعدادات الشريك. حدّث وحاول مرة أخرى.",
    languageCardTitle: "اللغة",
    languageCardText: "العربية أو الكردية أو الإنجليزية لمساحة عمل الشريك.",
    tabs: {
      profile: "الملف",
      dispatch: "الإرسال",
      fees: "رسوم التوصيل",
      preferences: "التفضيلات",
      system: "النظام",
    },
    fees: {
      title: "رسوم التوصيل",
      hint: "شرائح المسافة التي تسعّر توصيلات العملاء المرتبطين. التطبيق والذكاء الاصطناعي يقرآن هذه الأسعار نفسها.",
      add: "إضافة شريحة",
      save: "حفظ الرسوم",
      saved: "تم حفظ رسوم التوصيل",
      saveFailed: "تعذّر حفظ رسوم التوصيل.",
      empty: "لا شرائح بعد — أضف الأولى.",
      minKm: "أدنى كم",
      maxKm: "أقصى كم",
      fee: "الرسم",
      driverAmount: "أجر السائق",
      margin: "الهامش",
      remove: "حذف الشريحة",
      calculator: "حاسبة الرسوم",
      calculatorHint: "حرّك المسافة لمعاينة الرسم.",
      testDistance: "مسافة تجريبية: {distance}",
      noBand: "لا توجد شريحة تغطي هذه المسافة.",
      result: "الرسم: {fee}",
      errors: {
        invalid: "أدخل أرقاماً صحيحة.",
        range: "الأقصى يجب أن يكون أكبر من الأدنى.",
        fee: "يجب أن يكون الرسم أكبر من 0.",
        driver: "أجر السائق يجب أن يكون رقماً صالحاً.",
        driverOverFee: "أجر السائق لا يمكن أن يتجاوز رسم العميل.",
        overlap: "الشرائح متداخلة — عدّل المسافات.",
      },
    },
    profile: {
      title: "ملف الشريك",
      hint: "هوية النشاط لهذا الشريك. الاسم وبيانات الاتصال تُدار مركزياً.",
      logo: "الشعار",
      logoHint: "حقل العلامة التجارية الوحيد القابل للتعديل عبر RPC الملف.",
      changeLogo: "تغيير الشعار",
      uploading: "جاري الرفع…",
      businessInfo: "معلومات النشاط",
      businessHint: "للقراءة فقط من ملف الشريك.",
      name: "اسم الشريك",
      slug: "المعرّف",
      status: "الحالة",
      contactEmail: "بريد التواصل",
      displayName: "اسم العرض",
      role: "دورك",
      toastSaved: "تم تحديث الشعار.",
    },
    dispatch: {
      title: "إعدادات الإرسال",
      hint: "أعلام إعداد فقط — لا تغيّر منطق التعيين في هذه الواجهة.",
      modeTitle: "وضع الإرسال",
      modeHint: "تعيين يدوي أو بث تلقائي لأسطول الشريك.",
      modeAria: "وضع الإرسال",
      manualTitle: "إرسال يدوي",
      manualHint: "اقبل وعيّن سائقي الشريك بنفسك.",
      bypassTitle: "تجاوز (بث تلقائي)",
      bypassHint: "قبول وبث تلقائي لجميع سائقي الشريك النشطين.",
      fallbackTitle: "احتياطي OELOX",
      fallbackHint: "السماح بطلب سائقي OELOX عند عدم تغطية الأسطول.",
      fallbackToggle: "احتياطي تلقائي (OELOX)",
      fallbackDesc: "علم إعداد فقط. إجراءات الطلب ما زالت تتبع قواعد الخادم.",
      driverModeTitle: "وضع السائق الافتراضي",
      driverModeHint: "يُطبّق على السائقين الجدد ومن لا وضع خاص به.",
      driverModeOwnHint: "يحتفظ الشريك كامل رسم التوصيل.",
      driverModePaidHint: "يُتابع رسم السائق في محفظة السائق.",
      payTitle: "أجر السائق لكل رحلة",
      payHint:
        "المبلغ الافتراضي المدفوع للسائق عن كل رحلة توصيل مكتملة (IQD). يمكن تجاوزه لكل سائق.",
      payLabel: "الأجر الافتراضي لكل رحلة (IQD)",
      payPlaceholder: "مثال 2000",
      paySave: "حفظ الأجر",
      paySaved: "تم تحديث الأجر الافتراضي للسائق.",
      payDriverHint:
        "يُستخدم عندما لا يُحدد أجر خاص للسائق. يُثبّت الأجر على كل رحلة عند اكتمالها.",
      toastSaved: "تم حفظ إعدادات الإرسال.",
    },
    preferences: {
      title: "التفضيلات التشغيلية",
      hint: "تفضيلات واجهة فقط تُحفظ في هذا المتصفح. بلا كتابة للخادم.",
      stageTitle: "تركيز مرحلة الطلب الافتراضية",
      stageHint: "تفضيل عرض فقط — لا يغيّر مسار الطلب.",
      stageAll: "كل المراحل",
      stagePending: "قيد الانتظار",
      stageProcessing: "قيد المعالجة",
      stageReady: "جاهز",
      notifyTitle: "الإشعارات",
      notifyHint: "مفاتيح محلية لتنبيهات مستقبلية — غير مربوطة بالدفع بعد.",
      notifyOrders: "طلبات جديدة",
      notifyOrdersDesc: "إبراز عند وصول طلبات شريك جديدة.",
      notifyDrivers: "تحديثات السائقين",
      notifyDriversDesc: "حالة الاتصال أو التعيين للسائقين.",
      notifyFallback: "تنبيهات احتياطي OELOX",
      notifyFallbackDesc: "عند استخدام الاحتياطي أو عدم توفره.",
      toastSaved: "تم حفظ التفضيلات على هذا الجهاز.",
    },
    system: {
      title: "معلومات النظام",
      hint: "لقطة تشغيلية للقراءة فقط لهذا الشريك.",
      partnerId: "معرّف الشريك",
      partnerStatus: "حالة الشريك",
      activeClients: "العملاء النشطون",
      clientsValue: "{active} نشط / {total} إجمالي",
      activeDrivers: "السائقون النشطون",
      driversValue: "{fleet} أسطول / {online} متصل",
      dispatchMode: "وضع الإرسال",
      bypassLabel: "تجاوز (بث تلقائي)",
      manualLabel: "يدوي",
      fallback: "احتياطي OELOX",
      driverMode: "وضع السائق الافتراضي",
      oeloxTripFee: "رسم OELOX لكل رحلة",
      oeloxBalance: "رصيد محفظة OELOX",
      oeloxLimit: "الحد السلبي لـ OELOX",
      oeloxWalletNote:
        "يخصم OELOX رسم كل رحلة من هذه المحفظة؛ الشحن يتم عبر دعم OELOX.",
    },
  },
  accounts: {
    eyebrow: "بيانات دخول الحساب",
    username: "اسم المستخدم",
    password: "كلمة المرور",
    initialPassword: "كلمة المرور الأولية",
    newPassword: "كلمة المرور الجديدة",
    passwordPlaceholder: "8 أحرف على الأقل",
    passwordMin: "استخدم 8 أحرف على الأقل.",
    generate: "توليد",
    show: "إظهار كلمة المرور",
    hide: "إخفاء كلمة المرور",
    shownOnce:
      "تُعرض كلمة المرور الآن فقط. تُخزَّن كتشفير آمن ولا يمكن عرضها مرة أخرى — استخدم «إعادة تعيين كلمة المرور» إذا فُقدت.",
    done: "تم، حفظتها",
    checking: "جارٍ التحقق…",
    available: "متاح",
    resetTitle: "إعادة تعيين كلمة المرور",
    resetHint:
      "اختر كلمة مرور جديدة لهذا الحساب. لا يمكن استرجاع كلمة المرور الحالية؛ وتُعرض الجديدة مرة واحدة.",
    resetCta: "تعيين كلمة المرور",
    resetAction: "إعادة تعيين كلمة المرور",
    usernameLocked:
      "مقفل — لا يمكن تغيير اسم مستخدم الدخول بعد التفعيل.",
  },
  errors: {
    walletDuesUnsettled: "مستحقات محفظة العميل غير مسوّاة — يجب التسوية أولاً.",
    walletCashExceeds: "المبلغ يتجاوز النقد الموجود بحوزة السائق.",
    walletPayoutExceeds: "المبلغ يتجاوز المستحق للدفع.",
    walletScopeExceeds: "المبلغ يتجاوز المتبقي في هذا البند.",
    walletScopeInvalid: "نطاق تسليم غير صالح.",
    walletKindInvalid: "نوع التسوية غير صالح.",
    partnerClientNotFound: "هذا العميل غير مرتبط بحسابك.",
    requestFailed: "فشل الطلب.",
    usernameTaken: "اسم المستخدم مستخدم بالفعل. جرّب اسماً آخر.",
    usernameInvalid:
      "اسم مستخدم غير صالح. استخدم 3–30 حرفاً: a-z، 0-9، شرطة فقط.",
    clientNotFound: "لم يتم العثور على العميل لهذا الشريك.",
    forbidden: "لا تملك صلاحية لهذا الإجراء.",
    somethingWrong: "حدث خطأ ما.",
    roleCannotChange: "دورك لا يسمح بتغيير هذه الإعدادات.",
    partnerAccessDenied: "تم رفض وصول الشريك.",
    invalidDispatchMode: "وضع إرسال غير صالح.",
    fallbackDisabled: "احتياطي OELOX معطّل لهذا الشريك.",
    driversAvailable: "سائقو الشريك متاحون — عيّن من أسطولك.",
    noOeloxDrivers: "لا يوجد سائقو OELOX متاحون الآن.",
    acceptFirst: "اقبل الطلب قبل تعيين سائق.",
    walletBelowLimit:
      "رصيد المحفظة تحت حد الكريدت — تم إيقاف الإرسال. اطلب من OELOX تعبئة المحفظة للمتابعة.",
    noDispatchAuthority: "لا توجد صلاحية إرسال لهذا النشاط.",
    alreadyAssigned: "الطلب معيّن لسائق آخر.",
    driverFull:
      "وصل السائق للحد الأقصى — طلبان معاً (طلبات نفس المتجر غير محدودة).",
    groupRound:
      "هذا الطلب جزء من جولة واحدة — اقبل أو ارفض أو أرسل الجولة كاملة من بطاقة المجموعة (تطبيق الجوال).",
    alreadyAccepted: "الطلب مقبول بالفعل.",
    orderClosed: "الطلب مغلق بالفعل.",
    driverNotInFleet: "هذا السائق ليس في أسطول الشريك.",
    nameRequired: "اسم السائق مطلوب.",
    phoneInvalid: "أدخل رقم هاتف صالحاً.",
    phoneTaken: "رقم الهاتف مستخدم مسبقاً.",
    driverOtherPartner: "هذا السائق ينتمي إلى شريك آخر بالفعل.",
    driverInactive: "هذا السائق غير مُفعّل.",
    driverActiveOrders: "لدى السائق توصيلات جارية — أكملها أو أعد تعيينها أولاً.",
    vehicleInvalid: "نوع مركبة غير صالح.",
    driverNotFound: "السائق غير موجود في أسطولك.",
    driverModeInvalid: "وضع سائق غير صالح.",
    amountInvalid: "أدخل مبلغاً غير صفري.",
    amountTooLarge: "المبلغ كبير جداً.",
    invalidTripFee: "أدخل مبلغاً صحيحاً (0 أو أكثر).",
    slugTaken: "هذا الرابط العام مستخدم بالفعل لمتجر آخر.",
    slugInvalid:
      "رابط غير صالح. استخدم 3–60 حرفاً لاتينياً صغيراً أو أرقاماً أو شرطات مفردة.",
    passwordTooWeak: "استخدم كلمة مرور من 8 أحرف على الأقل.",
    emailInvalid: "أدخل بريداً إلكترونياً صالحاً.",
    credentialsUnavailable: "هذا الحساب ليس له بيانات دخول بعد — أكمل التفعيل أولاً.",
    requiredFields: "املأ جميع الحقول المطلوبة.",
    notFound: "الحساب غير موجود لهذا الشريك.",
    rateLimited: "محاولات كثيرة. حاول لاحقاً.",
  },
};

export default ar;
